import { colors, headingFont, uiFont, catalogColors as COLORS } from '../theme';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CardMedia,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useCart } from '../context/CartContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ProductPresentation from './ProductPresentation';
import { buildWhatsAppUrl } from '../config/site';
import {
  getProductImageUrl,
  getProductImageSizes,
  getProductImageSrcSet,
  getProductDisplayName,
  getMediaTypeLabel,
  getProductPath,
  getProductConditionLabel,
  getProductMediaType,
  getProductPriceLabel,
  getProductStatusLabel,
  getProductVideos,
  getYoutubeEmbedUrl,
  isProductAvailable,
} from '../utils/product';


function Feed({
  product,
  mode = 'catalog',
  priority = false,
  eagerImage = false,
  imageSizes = getProductImageSizes('card'),
  compact = false,
  hideCard = false,
  detailOpen: controlledDetailOpen,
  onDetailClose,
  topRank,
}) {
  const { addToCart } = useCart();
  const { playProduct, playVideo, pausePlayback } = useAudioPlayer();
  const [openDetail, setOpenDetail] = useState(false);
  const [openTracks, setOpenTracks] = useState(false);
  const [activeModalVideo, setActiveModalVideo] = useState(null);
  const inlinePlayerRef = useRef(null);
  const detailIsOpen = controlledDetailOpen ?? openDetail;

  useEffect(() => {
    if (detailIsOpen || openTracks) pausePlayback();
  }, [detailIsOpen, openTracks, pausePlayback]);

  useEffect(() => {
    if (!detailIsOpen) setActiveModalVideo(null);
  }, [detailIsOpen]);

  useEffect(() => {
    if (activeModalVideo && detailIsOpen) {
      inlinePlayerRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [activeModalVideo, detailIsOpen]);

  const isShowcase = mode === 'showcase';
  const isCompact = compact && !isShowcase;

  const p = product;
  const displayTitle = getProductDisplayName(p);
  const inStock = isProductAvailable(p);
  const statusLabel = getProductStatusLabel(p);
  const conditionLabel = getProductConditionLabel(p);
  const mediaTypeLabel = getMediaTypeLabel(getProductMediaType(p));
  const priceLabel = getProductPriceLabel(p);
  const videos = getProductVideos(p);
  const cardMetadata = Array.from(
    new Set([p.artist, p.label || p.format].filter(Boolean))
  ).join(' · ');
  const hasVideos = videos.length > 0;
  const cardImageUrl = getProductImageUrl(p.image, 'card');
  const cardImageSrcSet = getProductImageSrcSet(p.image, 'card');
  const productPath = getProductPath(p);
  const activeModalEmbedUrl = getYoutubeEmbedUrl(activeModalVideo?.url);
  const parsedTopRank = Number(topRank);
  const publicTopRank =
    inStock &&
    Number.isInteger(parsedTopRank) &&
    parsedTopRank >= 1 &&
    parsedTopRank <= 10
      ? parsedTopRank
      : null;

  const previewLabel = useMemo(() => {
    if (!hasVideos) return 'Sin previews';
    if (videos.length === 1) return '1 preview';
    return `${videos.length} previews`;
  }, [hasVideos, videos.length]);

  const handleOpenDetails = () => {
    setOpenDetail(true);
  };

  const handleCloseDetails = () => {
    setActiveModalVideo(null);
    setOpenDetail(false);
    onDetailClose?.();
  };

  const handlePlayProduct = () => {
    playProduct(p);
    setOpenTracks(false);
  };

  const handlePlayDockVideo = (video) => {
    playVideo(p, video);
    setOpenTracks(false);
  };

  const handlePlayModalVideo = (video) => {
    if (!getYoutubeEmbedUrl(video?.url)) return;

    pausePlayback();
    setActiveModalVideo(video);
  };

  const handleOpenDetailsKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDetails();
    }
  };

  const handleWhatsApp = () => {
    const message = `Hola! Me interesa este disco:
${displayTitle}
Precio: ${priceLabel}
Soporte: ${mediaTypeLabel}
Condición: ${conditionLabel}
Estado: ${statusLabel}

Quería consultar si sigue disponible. Gracias!`;

    window.open(buildWhatsAppUrl(message), '_blank');
  };

  const actionIconSx = {
    width: 40,
    borderRadius: 1,
    height: 44,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: colors.surface,
    '&:hover': {
      borderColor: COLORS.violet,
      backgroundColor: COLORS.violetTint,
    },
  };

  const renderInlinePlayer = () => {
    // Remove the iframe as soon as the dialog closes, including controlled closes.
    if (!detailIsOpen || !activeModalVideo || !activeModalEmbedUrl) return null;

    return (
      <Box
        ref={inlinePlayerRef}
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: colors.surface,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.accent, fontWeight: 900, display: 'block' }}
            >
              PREVIEW
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {activeModalVideo.title || 'Preview'}
            </Typography>
          </Box>

          <Tooltip title="Detener preview" arrow>
            <IconButton
              aria-label="Detener preview del detalle"
              onClick={() => setActiveModalVideo(null)}
              size="small"
              sx={actionIconSx}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            minHeight: 200,
            backgroundColor: colors.button,
          }}
        >
          <iframe
            key={activeModalEmbedUrl}
            width="100%"
            height="100%"
            src={activeModalEmbedUrl}
            title={`${displayTitle} - ${activeModalVideo.title || 'Preview'}`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{
              border: 0,
              display: 'block',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderVideoRows = (playbackMode = 'dock') => {
    if (!hasVideos) {
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: `1px dashed ${COLORS.border}`,
            backgroundColor: colors.surface,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: COLORS.muted, fontWeight: 600 }}
          >
            Este disco no tiene previews cargados.
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.1}>
        {videos.map((video, index) => (
          <Box
            key={`${video.url}-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) auto auto', sm: '24px minmax(0, 1fr) auto auto' },
              gap: 0.75,
              alignItems: 'center',
              p: 0.75,
              borderRadius: 1,
              backgroundColor: colors.surface,
              border: `1px solid ${COLORS.border}`,
              transition: 'border-color 0.2s ease, transform 0.2s ease',
              '&:hover': {
                borderColor: '#c7b1de',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', sm: 'grid' },
                width: 24,
                height: 24,
                borderRadius: '50%',
                placeItems: 'center',
                fontSize: '0.78rem',
                fontWeight: 900,
                color: colors.accent,
                backgroundColor: colors.tint,
                border: '1px solid rgba(123,44,191,0.14)',
              }}
            >
              {index + 1}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: COLORS.text,
                  fontWeight: 800,
                  lineHeight: 1.3,
                  overflowWrap: 'anywhere',
                }}
              >
                {video.title || `Preview ${index + 1}`}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: COLORS.subtle,
                  display: 'block',
                  mt: 0.3,
                }}
              >
                YouTube preview
              </Typography>
            </Box>

            <Tooltip title="Reproducir" arrow>
              <IconButton
                aria-label={`Reproducir ${video.title || `preview ${index + 1}`}`}
                onClick={() =>
                  playbackMode === 'inline'
                    ? handlePlayModalVideo(video)
                    : handlePlayDockVideo(video)
                }
                sx={{
                  ...actionIconSx,
                  color: colors.white,
                  backgroundColor: COLORS.black,
                  borderColor: COLORS.black,
                  '&:hover': {
                    backgroundColor: colors.hover,
                    borderColor: colors.hover,
                  },
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Abrir en YouTube" arrow>
              <IconButton
                aria-label={`Abrir ${video.title || `preview ${index + 1}`} en YouTube`}
                onClick={() => window.open(video.url, '_blank')}
                sx={actionIconSx}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <>
      {!hideCard && (
        <Card
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          background: COLORS.bg,
          color: COLORS.text,
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 12px 28px rgba(0,0,0,0.045)',
          transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
          overflow: 'hidden',
          '&:hover': {
            borderColor: colors.accent,
            boxShadow: '0 16px 34px rgba(0,0,0,0.075)',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
          }}
          role="button"
          tabIndex={0}
          aria-label={`Ver detalle de ${displayTitle}`}
          onClick={handleOpenDetails}
          onKeyDown={handleOpenDetailsKeyDown}
        >
          <CardMedia
            component="img"
            image={cardImageUrl}
            srcSet={cardImageSrcSet}
            sizes={imageSizes}
            alt={displayTitle}
            width={420}
            height={420}
            loading={priority || eagerImage ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : undefined}
            decoding="async"
            sx={{
              height: 'auto', width: '100%', aspectRatio: '1 / 1',
              objectFit: 'cover',
              backgroundColor: COLORS.bgSoft,
              filter: inStock ? 'none' : 'grayscale(18%)',
              opacity: inStock ? 1 : 0.84,
            }}
          />

          {publicTopRank && (
            <Chip
              label={`Top ${mediaTypeLabel} #${publicTopRank}`}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: 'rgba(17,17,17,0.88)',
                color: colors.white,
                fontWeight: 900,
                height: { xs: 22, sm: 24, md: isCompact ? 20 : 24 },
                '& .MuiChip-label': {
                  px: { xs: 0.9, sm: 1.1, md: isCompact ? 0.75 : 1.1 },
                  fontSize: {
                    xs: '0.64rem',
                    sm: '0.72rem',
                    md: isCompact ? '0.62rem' : '0.72rem',
                  },
                },
              }}
            />
          )}

          {!inStock && (
            <Chip
              label="Sin stock"
              size="small"
              sx={{
                position: 'absolute',
                top: 36,
                left: 8,
                backgroundColor: colors.button,
                color: colors.white,
                fontWeight: 800,
                height: { xs: 22, sm: 24, md: isCompact ? 20 : 24 },
                '& .MuiChip-label': {
                  px: { xs: 0.9, sm: 1.2, md: isCompact ? 0.7 : 1.2 },
                  fontSize: {
                    xs: '0.65rem',
                    sm: '0.75rem',
                    md: isCompact ? '0.62rem' : '0.75rem',
                  },
                },
              }}
            />
          )}
        </Box>

          {hasVideos && <IconButton aria-label={`Escuchar ${displayTitle}`} onClick={handlePlayProduct}
            sx={{ position: 'absolute', bottom: 10, right: 10, width: 44, height: 44, backgroundColor: colors.page,
              color: colors.white, border: `1px solid ${colors.secondary}`, '&:hover': { backgroundColor: colors.cta } }}>
            <PlayArrowIcon /></IconButton>}
          <Box sx={{ position: 'absolute', top: publicTopRank ? 38 : 8, left: 8, right: 8, pointerEvents: 'none',
            fontFamily: uiFont, fontSize: '0.62rem', color: colors.white }}>
            <Box component="span" sx={{ backgroundColor: 'rgba(8,8,11,0.9)', px: 0.8, py: 0.5 }}>{mediaTypeLabel} / {conditionLabel}</Box>
          </Box>
        </Box>

        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0.6, sm: 0.75, md: isCompact ? 0.6 : 0.75 },
            flexGrow: 1,
            p: { xs: 1.25, sm: 1.5, md: isCompact ? 1.25 : 1.5 },
            '&:last-child': {
              pb: { xs: 1, sm: 1.25 },
            },
          }}
        >
          <Box
            onClick={handleOpenDetails}
            onKeyDown={handleOpenDetailsKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Ver detalle de ${displayTitle}`}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{
                fontFamily: headingFont,
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.015em',
                color: COLORS.text,
                lineHeight: 1.2,
                fontSize: {
                  xs: isShowcase ? '1rem' : '1.1rem',
                  sm: '1.3rem',
                  md: isCompact ? '1.15rem' : '1.3rem',
                },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                minHeight: '2.4em',
                maxHeight: '2.4em',
              }}
            >
              {displayTitle}
            </Typography>

            <Typography
              variant="caption"
              title={cardMetadata}
              sx={{
                color: COLORS.muted,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mt: 0.25,
                textTransform: 'uppercase',
                letterSpacing: 0.45,
                minHeight: '1.4em',
                fontSize: {
                  xs: isShowcase ? '0.56rem' : '0.62rem',
                  sm: '0.75rem',
                  md: isCompact ? '0.64rem' : '0.75rem',
                },
                lineHeight: 1.4,
              }}
            >
              {cardMetadata || '\u00a0'}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: '#e8e1d3',
                mt: 0.75,
                fontSize: {
                  xs: isShowcase ? '0.9rem' : '0.96rem',
                  sm: '1.28rem',
                  md: isCompact ? '1.04rem' : '1.28rem',
                },
                lineHeight: 1,
              }}
            >
              {priceLabel}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 0.45, sm: 0.9, md: isCompact ? 0.4 : 0.9 },
              mt: 'auto',
              pt: { xs: 0.25, md: isCompact ? 0 : 0.25 },
            }}
          >
            <Tooltip title={hasVideos ? 'Ver tracks / previews' : 'Sin previews'} arrow>
              <span>
                <IconButton
                  aria-label={hasVideos ? `Ver previews de ${displayTitle}` : `Sin previews para ${displayTitle}`}
                  onClick={() => setOpenTracks(true)}
                  disabled={!hasVideos}
                  sx={{
                    ...actionIconSx,
                    color: hasVideos ? COLORS.text : colors.muted,
                    opacity: hasVideos ? 1 : 0.55,
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={inStock ? 'Agregar al carrito' : 'Sin stock'} arrow>
              <span>
                <IconButton
                  aria-label={inStock ? `Agregar ${displayTitle} al carrito` : `${displayTitle} sin stock`}
                  onClick={() => addToCart(p)}
                  disabled={!inStock}
                  sx={{
                    ...actionIconSx,
                    color: inStock ? COLORS.text : colors.muted,
                    opacity: inStock ? 1 : 0.55,
                  }}
                >
                  <AddShoppingCartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Ver pagina del disco" arrow>
              <IconButton
                component={Link}
                to={productPath}
                aria-label={`Ver pagina de ${displayTitle}`}
                sx={actionIconSx}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

        </CardContent>
        </Card>
      )}

      <Dialog
        open={openTracks}
        onClose={() => setOpenTracks(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: COLORS.paper,
            color: COLORS.text,
            borderRadius: { xs: 3, sm: 4 },
            border: `1px solid ${COLORS.border}`,
            m: { xs: 1.5, sm: 4 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            borderBottom: `1px solid ${COLORS.border}`,
            backgroundColor: colors.surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>
              Previews
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: COLORS.muted, mt: 0.3, display: 'block' }}
            >
              {displayTitle}
            </Typography>
          </Box>

          <Chip
            label={previewLabel}
            size="small"
            sx={{
              backgroundColor: hasVideos ? colors.surface : colors.elevated,
              color: hasVideos ? colors.accent : COLORS.text,
              border: `1px solid ${hasVideos ? COLORS.border : colors.controlBorder}`,
              fontWeight: 800,
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 2.2 }}>{renderVideoRows()}</DialogContent>
      </Dialog>

      <Dialog
        open={detailIsOpen}
        onClose={handleCloseDetails}
        maxWidth={false}
        aria-label="Detalle del disco"
        fullWidth
        PaperProps={{
          sx: {
            background: colors.surface,
            color: COLORS.text,
            borderRadius: 1,
            border: `1px solid ${COLORS.border}`,
            m: { xs: 1.5, sm: 3 },
            width: 'calc(100% - 24px)',
            maxWidth: 1040,
            maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100% - 64px)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            borderBottom: `1px solid ${COLORS.border}`,
            backgroundColor: colors.surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: { xs: 1.4, sm: 2 },
            pr: { xs: 1, sm: 1.5 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                display: 'block',
                fontWeight: 900,
                lineHeight: 1.1,
                fontFamily: uiFont,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                pr: 1,
              }}
            >
              Detalle del disco
            </Typography>

          </Box>

          <Tooltip title="Cerrar" arrow>
            <IconButton
              aria-label="Cerrar detalle"
              onClick={handleCloseDetails}
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: colors.surface,
                '&:hover': {
                  borderColor: COLORS.violet,
                  backgroundColor: COLORS.violetTint,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <ProductPresentation product={p} showEmptySections
            actions={<>
              {hasVideos && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handlePlayModalVideo(videos[0])}
                  sx={{
                    backgroundColor: COLORS.black,
                    borderRadius: 1,
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    '&:hover': { backgroundColor: colors.hover },
                  }}
                >
                  Escuchar
                </Button>
              )}

              {p.instagramUrl && (
                <Button
                  variant="outlined"
                  startIcon={<InstagramIcon />}
                  onClick={() => window.open(p.instagramUrl, '_blank')}
                  sx={{
                    borderColor: COLORS.border,
                    color: COLORS.text,
                    borderRadius: 1,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    '&:hover': {
                      borderColor: COLORS.violet,
                      backgroundColor: colors.tint,
                    },
                  }}
                >
                  Instagram
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsApp}
                sx={{
                  borderColor: COLORS.violet,
                  color: colors.accent,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  '&:hover': {
                    borderColor: COLORS.violetSoft,
                    backgroundColor: colors.tint,
                  },
                }}
              >
                Consultar
              </Button>

              <Button
                component={Link}
                to={productPath}
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                sx={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: COLORS.violet,
                    backgroundColor: colors.tint,
                  },
                }}
              >
                Ver pagina
              </Button>

              <Button
                variant="outlined"
                onClick={() => addToCart(p)}
                disabled={!inStock}
                sx={{
                  borderColor: COLORS.border,
                  color: COLORS.text,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: COLORS.text,
                    backgroundColor: COLORS.bgSoft,
                  },
                }}
              >
                Agregar al carrito
              </Button>

            </>}
            previews={<Stack spacing={1.5}>{renderInlinePlayer()}{renderVideoRows('inline')}</Stack>}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Feed;
